// Content load handler
// - Fügt Klassen bei DOMContentLoaded / load hinzu
// - Lazy-load für Elemente mit `data-src`
// - Preloader (#preloader) bleibt, bis das Hero-Video ein erstes Bild hat

import { lazyLoadImages, captureVideoSources, setupLazyVideos, waitForVideo } from './inc/lazyLoad';
import { initMobileMenu } from './inc/mobileMenu';
import { initScrollAnimations } from './inc/scrollAnimations';
import { initScrollMorph } from './inc/scrollMorph';
import { initScrollHeading } from './inc/scrollHeading';
import { initCountUp } from './inc/countUp';
import { initOpeningNotice } from './inc/openingNotice';

// Längste Wartezeit des Preloaders auf das Hero-Video, gerechnet ab Seitenaufruf
const PRELOADER_MAX_WAIT = 4000;

function hidePreloader(): void {
	// .loaded blendet den Preloader per CSS aus, danach wird er entfernt
	document.documentElement.classList.add('loaded');
	document.body.classList.add('loaded');
	const pre = document.getElementById('preloader');
	if (pre) window.setTimeout(() => pre.remove(), 400);
}

function initHeroAnimation(): void {
	document.querySelectorAll('#teaser-index, #teaser').forEach((teaser) => {
		teaser.classList.add('animate-in');
	});
}

function initOpeningHoursToggle(): void {
	const icon = document.querySelector<HTMLButtonElement>('.opening-hours-icon');
	const panel = document.querySelector<HTMLElement>('#opening-hours');
	if (!icon || !panel) return;

	const setOpen = (open: boolean) => {
		panel.classList.toggle('open', open);
		icon.setAttribute('aria-expanded', String(open));
	};

	// Echter <button>: Enter und Leertaste lösen den Klick von selbst aus
	icon.addEventListener('click', () => setOpen(!panel.classList.contains('open')));

	// Escape schließt das Panel und gibt den Fokus an den Button zurück
	panel.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === 'Escape' && panel.classList.contains('open')) {
			setOpen(false);
			icon.focus();
		}
	});
}

function onDOMContentLoaded(): void {
	document.documentElement.classList.add('dom-ready');
	document.body.classList.add('dom-ready');

	// LazyLoad zuerst
	lazyLoadImages(document);

	// Video-Pfade aus HTML auslesen und speichern
	captureVideoSources();

	// Hero-Video sofort laden, der Preloader wartet darauf
	const heroVideo = document.querySelector<HTMLVideoElement>('#headerVideo');
	if (heroVideo) setupLazyVideos([heroVideo]);

	initMobileMenu();
	initOpeningHoursToggle();
}

async function onWindowLoad(): Promise<void> {
	// Preloader erst ausblenden, wenn das Hero-Video ein Bild hat (höchstens PRELOADER_MAX_WAIT)
	const heroVideo = document.querySelector<HTMLVideoElement>('#headerVideo');
	if (heroVideo) await waitForVideo(heroVideo, PRELOADER_MAX_WAIT - performance.now());
	hidePreloader();

	// Hero-Animation sofort triggern
	initHeroAnimation();

	// Animationen erst NACH Preloader-Entfernung
	initScrollAnimations();
	initCountUp();

	// Scroll-Morph initialisieren
	initScrollMorph();

	// Scroll-Heading initialisieren
	initScrollHeading();

	// Übrige Videos (z. B. Logo-Video) erst jetzt laden
	setupLazyVideos();

	// Hinweis-Fenster erst zeigen, wenn der Preloader weg ist
	initOpeningNotice();
}

export function initContentLoadHandler(): void {
	if (document.readyState === 'complete') {
		onDOMContentLoaded();
		onWindowLoad();
		return;
	}

	if (document.readyState === 'interactive') {
		onDOMContentLoaded();
		window.addEventListener('load', onWindowLoad, { once: true });
		return;
	}

	window.addEventListener('DOMContentLoaded', onDOMContentLoaded, { once: true });
	window.addEventListener('load', onWindowLoad, { once: true });
}

// Auto-init when module is imported
initContentLoadHandler();

